import React, { useState, useEffect, useCallback, useRef } from 'react';
import Button from '../../BaseComponents/Button';
import AddFileModal from './AddFileModal';
import FilesTable from './FilesTable';
import { getMemberFiles, deleteMemberFile } from '../../../api/members';
import './Files.scss';

interface FilesTabProps {
    memberId?: string;
}

const FilesTab: React.FC<FilesTabProps> = ({ memberId }) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [files, setFiles] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 10;
    const offsetRef = useRef(0);

    const fetchFiles = useCallback(async (isInitial = true) => {
        if (!memberId) return;

        try {
            if (isInitial) {
                setIsLoading(true);
                offsetRef.current = 0;
            } else {
                setIsFetchingMore(true);
            }

            const currentOffset = isInitial ? 0 : offsetRef.current + limit;
            const response = await getMemberFiles(memberId, currentOffset, limit);

            const newFiles = response?.files || [];
            if (isInitial) {
                setFiles(newFiles);
                offsetRef.current = 0;
            } else {
                setFiles(prev => [...prev, ...newFiles]);
                offsetRef.current = currentOffset;
            }

            setTotalCount(response?.total_count || 0);
            setHasMore(newFiles.length === limit);
        } catch (error) {
            console.error('Failed to fetch files:', error);
        } finally {
            setIsLoading(false);
            setIsFetchingMore(false);
        }
    }, [memberId, limit]);

    useEffect(() => {
        fetchFiles(true);
    }, [fetchFiles]);

    const handleLoadMore = () => {
        if (!isLoading && !isFetchingMore && hasMore) {
            fetchFiles(false);
        }
    };

    const handleUpload = (fileData: any) => {
        // Refresh the file list after successful upload
        fetchFiles(true);
        setIsAddModalOpen(false);
    };

    const handleDelete = async (fileId: number | string) => {
        if (!memberId) return;

        try {
            await deleteMemberFile(memberId, fileId);
            fetchFiles(true); // Reset to first page after deletion
        } catch (error: any) {
            const serverMessage = error?.response?.data?.description || error?.response?.data?.message || error?.response?.data?.error || error?.message;
            alert(serverMessage || 'Failed to delete file');
            console.error('Failed to delete file:', error);
        }
    };

    return (
        <div className="files-tab-container">
            <div className="files-header sideBarPad2">
                <h2 className="files-title">Files ({totalCount})</h2>
                <Button
                    text="Upload"
                    width="114px"
                    isSecondary={true}
                    onClick={() => setIsAddModalOpen(true)}
                />
            </div>

            <FilesTable
                files={files}
                onDelete={handleDelete}
                isLoading={isLoading}
                isFetchingMore={isFetchingMore}
                memberId={memberId}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
            />

            <AddFileModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onUpload={handleUpload}
                memberId={memberId}
            />
        </div>
    );
};

export default FilesTab;

